import { useId, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { changeEvent, eventTimeInput, parseEventTime, type RoastEvent, type Workspace } from "./workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export function EventEditor({event, workspace, onChange, onClose, trigger}: {
  event: RoastEvent; workspace: Workspace; onChange: (workspace: Workspace) => void;
  onClose: () => void; trigger: HTMLElement | null;
}) {
  const id = useId();
  const timeRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const [time, setTime] = useState(eventTimeInput(event.elapsedMs));
  const [note, setNote] = useState(event.note);
  const [reason, setReason] = useState("");
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<{field: "time" | "reason"; message: string} | null>(null);

  function submit() {
    setError(null);
    if (!reason.trim()) {
      setError({field: "reason", message: "Explain why you are changing this event."}); reasonRef.current?.focus(); return;
    }
    try {
      // A note-only correction must not round a legacy sub-millisecond timestamp.
      const elapsedMs = removing || time === eventTimeInput(event.elapsedMs) ? event.elapsedMs : parseEventTime(time);
      onChange(changeEvent(workspace, removing ? null : {...event, elapsedMs, note}, event.id, reason));
      onClose();
    } catch (cause) {
      setError({field: removing ? "reason" : "time", message: cause instanceof Error ? cause.message : "Unable to change this event."});
      (removing ? reasonRef : timeRef).current?.focus();
    }
  }

  return <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
    <SheetContent className="w-full max-w-full overflow-y-auto overscroll-contain sm:max-w-md" onCloseAutoFocus={event => {
      event.preventDefault();
      if (trigger?.isConnected) trigger.focus(); else document.getElementById("event-log-focus")?.focus();
    }}>
      <SheetHeader className="pr-14"><SheetTitle>{removing ? "Remove event" : "Edit event"}: {event.name}</SheetTitle>
        <SheetDescription>{removing ? "The mark will leave the curve. Its original value and your reason will remain in local edit history." : "Correct the timestamp or observation. The original value stays in local edit history."}</SheetDescription>
      </SheetHeader>
      <form className="flex flex-1 flex-col gap-5 p-4 pt-0" noValidate onSubmit={e => { e.preventDefault(); submit(); }}>
        {!removing && <>
          <div className="space-y-2"><Label htmlFor={`${id}-time`}>Event time</Label>
            <Input ref={timeRef} id={`${id}-time`} name="event-time" maxLength={10} value={time} onChange={e => setTime(e.target.value)} aria-invalid={error?.field === "time"} aria-describedby={`${id}-time-hint${error?.field === "time" ? ` ${id}-error` : ""}`} autoComplete="off" />
            <p id={`${id}-time-hint`} className="text-xs text-muted-foreground">Use mm:ss or mm:ss.sss. Milestones follow roast order.</p>
          </div>
          <div className="space-y-2"><Label htmlFor={`${id}-note`}>Observation</Label><Input id={`${id}-note`} name="event-note" value={note} onChange={e => setNote(e.target.value)} maxLength={500} autoComplete="off" /></div>
        </>}
        <div className="space-y-2"><Label htmlFor={`${id}-reason`}>Reason for {removing ? "removal" : "correction"}</Label>
          <Input ref={reasonRef} id={`${id}-reason`} name="correction-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={300} aria-invalid={error?.field === "reason"} aria-describedby={error?.field === "reason" ? `${id}-error` : undefined} autoComplete="off" />
        </div>
        {error && <p id={`${id}-error`} className="text-sm text-destructive">{error.message}</p>}
        <div className="mt-auto flex flex-wrap gap-2 border-t pt-4">
          <Button type="submit" variant={removing ? "destructive" : "default"}>{removing ? "Confirm removal" : "Save correction"}</Button>
          <Button type="button" variant="outline" onClick={() => { if (removing) { setRemoving(false); setError(null); } else onClose(); }}>{removing ? "Keep event" : "Cancel"}</Button>
          {!removing && <Button type="button" variant="ghost" className="text-destructive" onClick={() => { setRemoving(true); setError(null); }}><Trash2 />Remove event</Button>}
        </div>
      </form>
    </SheetContent>
  </Sheet>;
}
